/**
 * KycMapping - stage 4/5 for PAN Card / Aadhaar Card / GST Certificate.
 * Attaches extracted identity fields onto an existing Customer or Supplier
 * record (PAN_ID / GSTNo). Vendor writes require the .NET API - see
 * document-hub.js /update-kyc for the exact limitation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;


export default function KycMapping({ extracted, mapping, setMapping, isDark, border, bgCard, text, textMut, category }) {
  const [entityType, setEntityType] = useState('customer');
  const [entities, setEntities] = useState([]);
  const [search, setSearch] = useState(extracted.name || extracted.legalName || '');
  const [selected, setSelected] = useState(null);
  const idValue = category === 'gst_certificate' ? extracted.gstin : (category === 'pan_card' ? extracted.panNo : extracted.aadhaarNo);

  useEffect(() => {
    const endpoint = entityType === 'customer' ? '/api/customer-master' : '/api/supplier-master';
    fetch(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => {
        let list = d.data || [];
        if (Array.isArray(list[0])) list = list.flat(3);
        setEntities(list);
      }).catch(() => {});
    setSelected(null);
  }, [entityType]);

  const entId = e => e?.CustomerId || e?.CUSTOMERID || e?.CustID || e?.SupplierId || e?.SUPPID || e?.Vend_uid || '';
  const entName = e => e?.CustomerName || e?.CUSTOMERNAME || e?.SupplierName || e?.SUPPNAME || e?.Vend_name || '';
  const filtered = entities.filter(e => entName(e).toLowerCase().includes(search.toLowerCase())).slice(0, 30);

  const buildMapping = useCallback(() => {
    const valid = !!selected && !!idValue;
    setMapping({
      __valid: valid,
      saveEndpoint: '/api/document-hub/update-kyc',
      savePayload: {
        entityType, entityId: entId(selected),
        panNo: category === 'pan_card' ? idValue : undefined,
        gstNo: category === 'gst_certificate' ? idValue : undefined,
      },
      summary: [
        `${entityType === 'customer' ? 'Customer' : 'Supplier'}: ${selected ? entName(selected) : 'not selected'} (${selected ? entId(selected) : ''})`,
        `Field: ${category === 'gst_certificate' ? 'GSTIN' : category === 'pan_card' ? 'PAN' : 'Aadhaar'} = ${idValue || 'NOT FOUND'}`,
      ],
      warning: entityType === 'vendor' ? 'Vendor KYC updates require the .NET API - will fail gracefully in offline/demo mode.' : (!idValue ? 'Could not read a valid ID number from this document.' : undefined),
    });
  }, [selected, entityType, idValue, category, setMapping]);

  useEffect(() => { buildMapping(); }, [selected, entityType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ padding: 12, borderRadius: 8, background: isDark ? '#1a1a1a' : '#f8fafc', marginBottom: 16, fontSize: 12, color: text }}>
        Extracted <strong>{category === 'gst_certificate' ? 'GSTIN' : category === 'pan_card' ? 'PAN' : 'Aadhaar No.'}</strong>: <span style={{ color: '#8b5cf6', fontWeight: 700 }}>{idValue || 'not found'}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['customer', 'vendor'].map(t => (
          <button key={t} data-testid={`doc-hub-kyc-entity-${t}`} onClick={() => setEntityType(t)}
            style={{ flex: 1, padding: '8px', borderRadius: 8, border: `1px solid ${entityType === t ? '#8b5cf6' : border}`, background: entityType === t ? '#8b5cf620' : 'transparent', color: entityType === t ? '#8b5cf6' : textMut, fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>
            {t === 'vendor' ? 'Supplier' : 'Customer'}
          </button>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: textMut }} />
        <input data-testid="doc-hub-kyc-search" value={search} onChange={e => { setSearch(e.target.value); setSelected(null); }}
          placeholder={`Search ${entityType}...`}
          style={{ width: '100%', padding: '9px 10px 9px 28px', borderRadius: 8, border: `1px solid ${selected ? '#22c55e' : border}`, background: bgCard, color: text, fontSize: 12, boxSizing: 'border-box' }} />
      </div>
      {!selected && search && (
        <div style={{ marginTop: 6, maxHeight: 160, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 8 }}>
          {filtered.map((e, i) => (
            <div key={i} data-testid={`doc-hub-kyc-option-${i}`} onClick={() => { setSelected(e); setSearch(entName(e)); }}
              style={{ padding: '8px 12px', fontSize: 12, color: text, cursor: 'pointer', borderTop: i > 0 ? `1px solid ${border}` : 'none' }}>
              {entName(e)} <span style={{ color: textMut }}>({entId(e)})</span>
            </div>
          ))}
          {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 11, color: textMut }}>No matches</div>}
        </div>
      )}
    </div>
  );
}
